import { Contact, ContactSummary, CustomerAccountTypes } from './contact';
import { AccessType, CreditAccountType, CreditLimitOption } from './contact.options';
import { ContactType } from './contact.type';
import { TextUtility } from '../../utilities/text.utility';
import * as Enumerable from 'linq';

export class ContactUtility {

    // Trim the account's key text fields on save so names sort correctly.
    public static normalizeText(request: Contact): void {

        if (!request) return;

        request.name = TextUtility.trimToNull(request.name);
        if (request.contact) {
            request.contact.firstName = TextUtility.trimToNull(request.contact.firstName);
            request.contact.lastName = TextUtility.trimToNull(request.contact.lastName);
            request.contact.name = TextUtility.trimToNull(request.contact.name);
            request.contact.email = TextUtility.trimToNull(request.contact.email);
        }
    }

    public static getContactSummary(contact: Contact): ContactSummary {

        if (!contact?._id)
            return null;

        return {
            _id: contact._id,
            name: contact?.name,
            firstName: contact?.firstName,
            lastName: contact?.lastName,
            phone: contact.phone,
            mobile: contact.mobile,
            workPhone: contact.workPhone,
            email: contact.email,
            address: contact?.address,
            type: contact.type,
            active: contact.active,
            subType: contact.subType,
            labels: contact?.labels,
            // sites: contact?.sites,
            title: contact?.title,
            primarySite: contact?.primarySite,
            username: contact?.username,
            parents: contact.parents,
            note: contact?.note,
            dispatching: contact?.dispatching
        };
    }

    public static getFullName(contact: Partial<Contact>): string {

        if (!contact) return null;

        const { name, firstName, lastName } = contact;

        return name?.trim?.() ?? [firstName, lastName].filter(x => !!x).map(x => x.trim()).join(' ');
    }

    public static isCustomer(type: ContactType): boolean {
        return CustomerAccountTypes.includes(type);
    }

    public static sendEmailByContactType(type: ContactType): boolean {

        const customerType = [
            ContactType.Employee,
            ContactType.LoginContact
        ];

        return customerType.includes(type);
    }

    public static getAccessType(contact: Contact): AccessType {

        const parent = Enumerable.from(contact.parents).firstOrDefault();

        const type = contact.type === ContactType.Employee
            ? ContactType.Employee
            : parent.type;

        let accessType = AccessType.Client;

        switch (type) {
            case ContactType.Employee: {
                accessType = AccessType.Employee;
                break;
            }
            case ContactType.Subcontractor: {
                accessType = AccessType.Subcontractor;
                break;
            }

            case ContactType.ServiceProvider:
            case ContactType.Individual:
            case ContactType.Business: {
                accessType = AccessType.Client;
                break;
            }

        }

        return accessType;

    }

    public static isSubcontractor(contact: Contact): boolean {

        const subcontractor = contact?.type === ContactType.LoginContact && Enumerable.from(contact?.parents).any(x => x.type === ContactType.Subcontractor);

        return subcontractor;


    }

    public static stringIsEmptyOrUndefined(value: string): boolean {

        if (!value) return true;

        if (value?.trim() === '') return true;

        return false;

    }

    public static filterUsers(users: Partial<Contact>[], searchText: string): Partial<Contact>[] | ContactSummary[] {
        searchText = searchText ? searchText.toLowerCase() : null;

        return Enumerable
            .from(users)
            .where(user => {

                if (!searchText) return true;

                const nameIndex = user.name
                    ? user.name.toLowerCase().indexOf(searchText, 0)
                    : -1;

                const emailIndex = user.email
                    ? user.email.toLowerCase().indexOf(searchText, 0)
                    : -1;

                const mobileIndex = user.mobile
                    ? user.mobile.toLowerCase().indexOf(searchText, 0)
                    : -1;

                const phoneIndex = user.phone
                    ? user.phone.toLowerCase().indexOf(searchText, 0)
                    : -1;

                return nameIndex > -1 || emailIndex > -1 || mobileIndex > -1 || phoneIndex > -1;
            })
            .orderBy(x => x.name)
            .toArray();
    }

    public static getUserIdWithTenantPrefix(userId: string, tenantName: string): string {
        return `${tenantName}-${userId}`;
    }


    public static isBlock(customer: Contact): boolean {
        if (!customer?.credit) {
            return false;
        }

        const { limitBlock, type } = customer?.credit;

        if (limitBlock?._id !== CreditLimitOption.Block || type === CreditAccountType.Prepaid) {
            return false;
        }

        const exceededLimit = this.exceedCreditLimit(customer);

        return exceededLimit;
    }

    public static isWarning(customer: Contact, treatBlockAsWarning = false) {
        if (!customer?.credit) {
            return false;
        }

        const { limitBlock, type } = customer?.credit;

        if ((limitBlock?._id === CreditLimitOption.Block && !treatBlockAsWarning) || type === CreditAccountType.Prepaid) {
            return false;
        }

        const exceededLimit = this.exceedCreditLimit(customer);

        return exceededLimit;
    }

    private static exceedCreditLimit(customer: Contact): boolean {

        const { limitAmount, enforceOverdue } = customer?.credit;

        if (limitAmount === 0) {
            return false;
        }

        const creditAmount = customer?.creditAmount;

        const hasOverDue = creditAmount?.overdue > 0;

        if (enforceOverdue && hasOverDue) {

            return hasOverDue;
        }

        return creditAmount?.outstanding > limitAmount;
    }

    public static contactGrouping(contacts: ContactSummary[]): ContactSummary[] {
        return Enumerable
            .from(contacts)
            .select((e: any) => {

                e.groupType = e.type === ContactType.Subcontractor
                    ? 'Contractor(s)' as any
                    : `${e.type}(s)`;

                return e;
            })
            .orderBy(e => e?.type)
            .thenBy(e => e?.name)
            .toArray();
    }

}
